import React, { useState, useEffect, useCallback, useRef } from "react"
import { Table, Input, Button, Modal, Tag, Tooltip, message } from "antd"
import { DeleteOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { translations } from "../../utils/Translation/staticTranslation"
import { Campaigns as CampaignsAPI } from "../../Apis/campaignsApi"
import "./Campaigns.css"

const PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 400

const formatFailedAt = (iso) => {
  if (!iso) return "—"
  const d = dayjs(iso)
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : "—"
}

/**
 * Server-paginated, searchable list of a campaign's failed recipients with
 * the ability to remove selected addresses (or all of them) from the campaign.
 *
 * Deletion is irreversible: the backend drops the address from the failure
 * list, from the selected recipients, and adds it to `excluded_emails` so it
 * is skipped on future sends.
 */
const FailedEmailsSection = ({ campaignId, readOnly = false, canDelete = true, onChanged }) => {
  const lang = localStorage.getItem("language") || "ar"
  const isAr = lang === "ar"
  const t = translations[lang]?.campaignsPage?.failed_emails || {}
  const common_btn = translations[lang]?.common_btn || {}

  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [rows, setRows] = useState([])
  const [count, setCount] = useState(0)
  const [totalFailed, setTotalFailed] = useState(0)
  const [excludedEmails, setExcludedEmails] = useState([])
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [selectedEmails, setSelectedEmails] = useState([])
  const debounceRef = useRef(null)

  const fetchFailedEmails = useCallback(
    async ({ page: targetPage = page, search: targetSearch = search } = {}) => {
      if (!campaignId) return
      setLoading(true)
      try {
        const response = await CampaignsAPI.getFailedEmails(campaignId, {
          search: targetSearch,
          page: targetPage,
          pageSize: PAGE_SIZE,
        })
        const data = response?.data || {}
        setRows(Array.isArray(data.results) ? data.results : [])
        setCount(Number(data.count) || 0)
        setTotalFailed(Number(data.total_failed) || 0)
        setExcludedEmails(Array.isArray(data.excluded_emails) ? data.excluded_emails : [])
      } catch (error) {
        console.error("Error fetching failed emails:", error)
        message.error(
          error.response?.data?.[isAr ? "messageArabic" : "message"] ||
            t.fetch_failed ||
            (isAr ? "فشل في جلب الرسائل الفاشلة" : "Failed to fetch failed emails")
        )
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignId, page, search, isAr]
  )

  // Reset everything when the campaign changes.
  useEffect(() => {
    setPage(1)
    setSearchInput("")
    setSearch("")
    setSelectedEmails([])
    setRows([])
    setCount(0)
  }, [campaignId])

  useEffect(() => {
    fetchFailedEmails({ page, search })
  }, [campaignId, page, search]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(debounceRef.current), [])

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchInput(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPage(1)
      setSearch(value.trim())
    }, SEARCH_DEBOUNCE_MS)
  }

  const handleSearchNow = () => {
    clearTimeout(debounceRef.current)
    setPage(1)
    setSearch(searchInput.trim())
  }

  const showError = (error, fallback) => {
    message.error(
      error?.response?.data?.[isAr ? "messageArabic" : "message"] ||
        error?.response?.data?.detail ||
        fallback
    )
  }

  const performDelete = async (payload) => {
    setDeleting(true)
    try {
      const response = await CampaignsAPI.deleteFailedEmails(campaignId, payload)
      const data = response?.data || {}
      const deletedCount = Number(data.deleted_count) || 0
      message.success(
        `${(isAr ? data.messageArabic : data.message) ||
          t.deleted_success ||
          (isAr ? "تمت الإزالة بنجاح" : "Removed successfully")} (${deletedCount})`
      )
      setSelectedEmails([])

      // If the current page emptied out, step back a page.
      const remaining = Number(data.remaining_failed)
      const nextPage =
        Number.isFinite(remaining) && remaining <= (page - 1) * PAGE_SIZE
          ? Math.max(1, page - 1)
          : page
      if (nextPage !== page) setPage(nextPage)
      else await fetchFailedEmails({ page, search })

      if (typeof onChanged === "function") onChanged()
    } catch (error) {
      console.error("Error deleting failed emails:", error)
      showError(
        error,
        t.delete_failed || (isAr ? "فشل إزالة الرسائل الفاشلة" : "Failed to remove failed emails")
      )
    } finally {
      setDeleting(false)
    }
  }

  const confirmDelete = ({ all }) => {
    if (readOnly || !canDelete) return
    const n = all ? totalFailed : selectedEmails.length
    if (!all && n === 0) return

    Modal.confirm({
      title: all
        ? t.confirm_delete_all_title || (isAr ? "إزالة جميع الرسائل الفاشلة؟" : "Remove all failed emails?")
        : t.confirm_delete_title || (isAr ? "إزالة الرسائل الفاشلة المحددة؟" : "Remove selected failed emails?"),
      content: (
        <div>
          <div>
            {all
              ? (t.confirm_delete_all_message ||
                  (isAr
                    ? "سيتم إزالة {count} عنوان بريد من هذه الحملة."
                    : "{count} email address(es) will be removed from this campaign.")).replace("{count}", n)
              : (t.confirm_delete_message ||
                  (isAr
                    ? "سيتم إزالة {count} عنوان بريد محدد من هذه الحملة."
                    : "{count} selected email address(es) will be removed from this campaign.")).replace("{count}", n)}
          </div>
          <div style={{ marginTop: 10, color: "#B42318", fontWeight: 500 }}>
            {t.confirm_delete_warning ||
              (isAr
                ? "ستتم إزالتهم من المستلمين وتجاهلهم في أي إرسال مستقبلي. لا يمكن التراجع عن هذا الإجراء."
                : "They will be removed from the recipients and skipped on any future send. This cannot be undone.")}
          </div>
        </div>
      ),
      okText: t.confirm_ok || (isAr ? "نعم، أزل" : "Yes, remove"),
      okType: "danger",
      cancelText: common_btn.cancel_btn || (isAr ? "إلغاء" : "Cancel"),
      centered: true,
      // The statistics modal sits at z-index 3000; keep the confirm above it.
      zIndex: 3100,
      onOk: () => performDelete(all ? { all: true } : { emails: selectedEmails }),
    })
  }

  const columns = [
    {
      title: t.email || (isAr ? "البريد الإلكتروني" : "Email"),
      dataIndex: "email",
      key: "email",
      width: 260,
      render: (text) => text || "—",
    },
    {
      title: t.reason || (isAr ? "السبب" : "Reason"),
      dataIndex: "reason",
      key: "reason",
      render: (text) => (
        <Tooltip title={text || ""}>
          <span className="error-message failed-email-reason">{text || "—"}</span>
        </Tooltip>
      ),
    },
    {
      title: t.failed_at || (isAr ? "وقت الفشل" : "Failed at"),
      dataIndex: "failed_at",
      key: "failed_at",
      width: 160,
      render: (v) => formatFailedAt(v),
    },
  ]

  const hasData = count > 0 || totalFailed > 0 || search

  return (
    <div className="failed-emails-section">
      <div className="failed-emails-header">
        <div />
        {!readOnly && canDelete && (
          <div className="failed-emails-actions">
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={selectedEmails.length === 0 || deleting}
              loading={deleting}
              onClick={() => confirmDelete({ all: false })}
            >
              {(t.delete_selected || (isAr ? "إزالة المحدد" : "Remove selected"))}
              {selectedEmails.length > 0 ? ` (${selectedEmails.length})` : ""}
            </Button>
            <Button
              danger
              type="primary"
              icon={<DeleteOutlined />}
              disabled={totalFailed === 0 || deleting}
              onClick={() => confirmDelete({ all: true })}
            >
              {t.delete_all || (isAr ? "إزالة الكل" : "Remove all")}
            </Button>
          </div>
        )}
      </div>

      {hasData ? (
        <>
          <div className="failed-emails-toolbar">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t.search_placeholder || (isAr ? "ابحث بالبريد أو السبب" : "Search by email or reason")}
              value={searchInput}
              onChange={handleSearchChange}
              onPressEnter={handleSearchNow}
              className="failed-emails-search"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchFailedEmails({ page, search })}
              disabled={loading}
              aria-label={t.refresh || (isAr ? "تحديث" : "Refresh")}
            />
          </div>

          <Table
            dataSource={rows}
            columns={columns}
            loading={loading}
            rowKey={(record, index) => record.email || `failed-${index}`}
            rowSelection={
              readOnly || !canDelete
                ? undefined
                : {
                    selectedRowKeys: selectedEmails,
                    onChange: (keys) => setSelectedEmails(keys),
                    preserveSelectedRowKeys: true,
                  }
            }
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: count,
              showSizeChanger: false,
              onChange: (p) => setPage(p),
              showTotal: (total) => `${total} ${t.items || (isAr ? "عنصر" : "items")}`,
            }}
            scroll={{ x: 760 }}
            className="logs-error-table failed-emails-table"
            locale={{
              emptyText: search
                ? t.no_results || (isAr ? "لا توجد نتائج مطابقة" : "No matching results")
                : t.empty || (isAr ? "لا توجد رسائل فاشلة" : "No failed emails"),
            }}
          />
        </>
      ) : (
        !loading && (
          <div className="logs-no-errors">
            <p>{t.empty || (isAr ? "لا توجد رسائل فاشلة" : "No failed emails")}</p>
          </div>
        )
      )}

      {excludedEmails.length > 0 && (
        <div className="failed-emails-excluded">
          <div className="failed-emails-excluded-title">
            {t.excluded_title || (isAr ? "عناوين مستبعدة من هذه الحملة" : "Excluded from this campaign")}
            <Tag className="failed-emails-count-tag">{excludedEmails.length}</Tag>
          </div>
          <div className="failed-emails-excluded-list">
            {excludedEmails.map((email) => (
              <Tag key={email}>{email}</Tag>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default FailedEmailsSection
